/**
 * Handle user geolocation
 *
 * @package ARS Webapp
 * @author René Schulze <schulze@thadeus-roth.de>
 */

import { inject } from 'vue';
import config from '?/Config';
import store from '?/Stores';
import emitter from '?/Composable/Events/Emitter';

/*
 * Calculate distance between two points in meter
 *
 * @param {object} p1 First object with lat and lng data
 * @param {object} p2 Second object with lat and lng data
 *
 * @return {int}
 */
export const calculateDistance = (p1, p2) => {
    const rEarth = 6371000;
    const toRad = (x) => {
        return parseFloat(x) * Math.PI / 180;
    };
    const haversine = (x) => {
        return (0.5 * (1 - Math.cos(x)));
    };
    let xp1 = {};
    let xp2 = {};

    xp1.lat = toRad(p1.lat);
    xp2.lat = toRad(p2.lat);
    xp1.lng = toRad(p1.lng);
    xp2.lng = toRad(p2.lng);

    let l1 = Math.cos(xp1.lat);
    let l2 = Math.cos(xp2.lat);

    let hv1 = haversine(xp2.lat - xp1.lat);
    let hv2 = l1 * l2 * haversine(xp2.lng - xp1.lng);
    let c = Math.asin(Math.sqrt(hv1 + hv2));

    return Math.ceil(2 * rEarth * c);
};


/*
 * Calculate distance to player in meter and stores data in stations
 *
 * @param {GeolocationPosition} position Position of player
 */
export const addDistanceToStations = (position) => {
    if (
        !position ||
        !Object.keys(store.getters.stations).length
    ) {
        return;
    }

    let stations = store.getters.stations;

    for (const key in store.getters.stations) {
        stations[key].distance = calculateDistance(
            {
                lat: stations[key].latitude,
                lng: stations[key].longitude
            },
            {
                lat: position.coords.latitude,
                lng: position.coords.longitude
            }
        );
    }

    store.commit('stations', stations);

    if (config.debug) {
        console.log(
            'Geolocation: Updated station distance',
            ', Stations:', stations
        );
    }
}


/*
 * Handle tracking of geolocation
 */
export default function geolocation() {
    if (!navigator.geolocation) {
        if (config.debug) {
            console.log(
                'Geolocation: No support'
            );
        }
        return;
    }

    /*
     * Handles the successful retrieving of position data
     *
     * @param {GeolocationPosition} position Position of player
     */
    const success = (position) => {
        store.commit('geolocation', position);

        addDistanceToStations(position);

        // @event geolocation-success
        emitter.emit('geolocation-success', position);

        if (config.debug) {
            console.log(
                'Geolocation: Success',
                'Position: ', position
            );
        }
    };

    /*
     * Handles errors while trying to retrieve position data
     *
     * @param {GeolocationPosition} position Position of player
     */
    const error = (error) => {
        // @event geolocation-failure
        emitter.emit('geolocation-failure', error);

        if (config.debug) {
            console.log(
                'Geolocation: Error',
                'Code: ' + error.code,
                'Message: ' + error.message
            );
        }
    };

    const options = {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000
    };

    navigator.geolocation.watchPosition(success, error, options);
};
